import { defineStore } from 'pinia';
import { watch, type WatchStopHandle } from 'vue';
import { isConsentManagementActive } from '@/Integrations/Consent/consentPolicy';
import {
  openExternalContentConsentUi,
  watchExternalContentMarketingConsent,
} from '@/Integrations/Consent/externalContentConsent';

const subscriptions: {
  externalContentConsent: (() => void) | null;
  consentRequirementWatch: WatchStopHandle | null;
} = {
  externalContentConsent: null,
  consentRequirementWatch: null,
};

export const useConsentStore = defineStore('pages-consent', {
  state: () => ({
    isInitialized: false,
    isConsentRequired: false,
    isExternalContentAllowed: true,
  }),
  getters: {
    showExternalContentHint(state): boolean {
      return state.isConsentRequired && !state.isExternalContentAllowed;
    },
  },
  actions: {
    syncExternalContentConsentState() {
      this.disposeExternalContentConsent();

      const consentRequired = isConsentManagementActive();
      this.isConsentRequired = consentRequired;

      if (!consentRequired) {
        this.isExternalContentAllowed = true;
        this.isInitialized = true;
        return;
      }

      this.isExternalContentAllowed = false;
      subscriptions.externalContentConsent = watchExternalContentMarketingConsent((allowed) => {
        this.isExternalContentAllowed = allowed;
      });
    },

    ensureExternalContentConsent() {
      if (this.isInitialized) {
        return;
      }

      subscriptions.consentRequirementWatch = watch(
        () => isConsentManagementActive(),
        () => {
          this.syncExternalContentConsentState();
        },
        { immediate: true },
      );

      this.isInitialized = true;
    },

    resetExternalContentConsent() {
      this.disposeConsentRequirementWatch();
      this.disposeExternalContentConsent();
      this.isInitialized = false;
      this.isConsentRequired = false;
      this.isExternalContentAllowed = true;
    },

    disposeExternalContentConsent() {
      if (subscriptions.externalContentConsent) {
        subscriptions.externalContentConsent();
        subscriptions.externalContentConsent = null;
      }
    },

    disposeConsentRequirementWatch() {
      if (subscriptions.consentRequirementWatch) {
        subscriptions.consentRequirementWatch();
        subscriptions.consentRequirementWatch = null;
      }
    },

    openConsentUi() {
      openExternalContentConsentUi();
    },
  },
});
